'use client';

import { Button, IconButton, Stack, TextField } from '@mui/material';

interface KeyValuePair {
  key: string;
  value: string;
}

interface KeyValuePairEditorProps {
  pairs: KeyValuePair[];
  onChange: (pairs: KeyValuePair[]) => void;
  keyPlaceholder: string;
  valuePlaceholder: string;
  disabled?: boolean;
}

export const KeyValuePairEditor = ({
  pairs,
  onChange,
  keyPlaceholder,
  valuePlaceholder,
  disabled = false,
}: KeyValuePairEditorProps) => {
  const effectivePairs = pairs.length === 0 ? [{ key: '', value: '' }] : pairs;

  const handleKeyChange = (index: number, newKey: string) => {
    const updated = effectivePairs.map((pair, i) =>
      i === index ? { ...pair, key: newKey } : pair
    );
    onChange(updated);
  };

  const handleValueChange = (index: number, newValue: string) => {
    const updated = effectivePairs.map((pair, i) =>
      i === index ? { ...pair, value: newValue } : pair
    );
    onChange(updated);
  };

  const handleRemove = (index: number) => {
    const updated = effectivePairs.filter((_, i) => i !== index);
    onChange(updated.length === 0 ? [{ key: '', value: '' }] : updated);
  };

  const handleAdd = () => {
    onChange([...effectivePairs, { key: '', value: '' }]);
  };

  return (
    <Stack gap='8px'>
      {effectivePairs.map((pair, index) => (
        <Stack key={index} direction='row' gap='8px' alignItems='center'>
          <TextField
            size='small'
            placeholder={keyPlaceholder}
            value={pair.key}
            onChange={(e) => handleKeyChange(index, e.target.value)}
            disabled={disabled}
            sx={{ flex: 1 }}
          />
          <TextField
            size='small'
            placeholder={valuePlaceholder}
            value={pair.value}
            onChange={(e) => handleValueChange(index, e.target.value)}
            disabled={disabled}
            sx={{ flex: 1 }}
          />
          <IconButton
            size='small'
            onClick={() => handleRemove(index)}
            disabled={disabled}
            aria-label='Remove row'
          >
            <i className='material-symbols-close-rounded' />
          </IconButton>
        </Stack>
      ))}
      <Button
        size='small'
        variant='text'
        startIcon={<i className='material-symbols-add-rounded' />}
        onClick={handleAdd}
        disabled={disabled}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add
      </Button>
    </Stack>
  );
};
